import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle scroll effect and mobile detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isManufacturer = user?.role === 'manufacturer';

  // Animation styles
  const animationStyles = `
    .nav-fade-in {
      animation: navFadeIn 0.3s ease-out forwards;
    }
    
    .nav-slide-down {
      animation: navSlideDown 0.3s ease-out forwards;
    }
    
    @keyframes navFadeIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes navSlideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .nav-link {
      position: relative;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .nav-link::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
      transition: width 0.3s ease;
    }
    
    .nav-link:hover::after {
      width: 100%;
    }
    
    .nav-link:hover {
      color: #3b82f6;
      transform: translateY(-1px);
    }
    
    .mobile-menu-enter {
      animation: mobileMenuEnter 0.3s ease-out forwards;
    }
    
    @keyframes mobileMenuEnter {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .desktop-nav {
        display: none !important;
      }
      .mobile-menu-btn {
        display: flex !important;
      }
    }

    @media (min-width: 769px) {
      .desktop-nav {
        display: flex !important;
      }
      .mobile-menu-btn {
        display: none !important;
      }
    }
  `;

  return (
    <>
      <style>{animationStyles}</style>
      <nav style={{
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 1)',
        backdropFilter: isScrolled ? 'blur(20px)' : 'none',
        borderBottom: isScrolled ? '1px solid rgba(226, 232, 240, 0.8)' : '1px solid #e2e8f0',
        padding: '0',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: isScrolled 
          ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
          : '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '72px'
          }}>
            {/* Logo Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              cursor: 'pointer'
            }}>
              <a href="/" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                textDecoration: 'none'
              }}>
                {/* Logo Icon */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <path d="M9 12l2 2 4-4"/>
                    <circle cx="12" cy="12" r="9"/>
                  </svg>
                </div>

                {/* Brand Name */}
                <div>
                  <div style={{
                    fontSize: isMobile ? '24px' : '28px',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 50%, #06b6d4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    letterSpacing: '-0.02em',
                    lineHeight: '1'
                  }}>
                    ScanOnce
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: '#64748b',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginTop: '-2px'
                  }}>
                    Enterprise
                  </div>
                </div>
              </a>

              {/* Version Badge */}
              <div style={{
                display: 'inline-block',
                backgroundColor: '#22c55e',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
              }}>
                2.0
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="desktop-nav" style={{ 
              display: isMobile ? 'none' : 'flex', 
              alignItems: 'center', 
              gap: '32px'
            }}>
              {isAuthenticated ? (
                <>
                  {/* User Welcome */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div style={{
                        color: '#1e293b',
                        fontSize: '14px',
                        fontWeight: '600',
                        lineHeight: '1'
                      }}>
                        {user?.firstName}
                      </div>
                      <div style={{
                        color: '#64748b',
                        fontSize: '11px',
                        textTransform: 'capitalize',
                        lineHeight: '1'
                      }}>
                        {isManufacturer ? 'Manufacturer' : 'Consumer'}
                      </div>
                    </div>
                  </div>

                  {/* Manufacturer Navigation */}
                  {isManufacturer && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '24px'
                    }}>
                      <a href="/manufacturer/dashboard" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"/>
                          <rect x="14" y="3" width="7" height="7"/>
                          <rect x="14" y="14" width="7" height="7"/>
                          <rect x="3" y="14" width="7" height="7"/>
                        </svg>
                        Dashboard
                      </a>

                      <a href="/manufacturer/products" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                        Products
                      </a>

                      <a href="/manufacturer/analytics" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 3v18h18"/>
                          <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                        </svg>
                        Analytics
                      </a>

                      <a href="/manufacturer/billing" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                          <line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                        Billing
                      </a>
                    </div>
                  )}

                  {/* Consumer Navigation */}
                  {!isManufacturer && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '24px'
                    }}>
                      <a href="/verify" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        Verify Products
                      </a>

                      <a href="/dashboard" className="nav-link" style={{
                        color: '#475569',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"/>
                          <rect x="14" y="3" width="7" height="7"/>
                          <rect x="14" y="14" width="7" height="7"/>
                          <rect x="3" y="14" width="7" height="7"/>
                        </svg>
                        Dashboard
                      </a>
                    </div>
                  )}

                  {/* Logout Button */}
                  <button
                    onClick={logout}
                    style={{
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      padding: '10px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ef4444';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#ef4444';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.color = '#475569';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16,17 21,12 16,7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Logout
                  </button>
                </>
              ) : (
                // Guest Navigation
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px'
                  }}>
                    <a href="/verify" className="nav-link" style={{
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="M21 21l-4.35-4.35"/>
                      </svg>
                      Verify Product
                    </a>

                    <a href="/about" className="nav-link" style={{
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '15px'
                    }}>
                      About
                    </a>

                    <a href="/manufacturer/register" className="nav-link" style={{
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 21h18"/>
                        <path d="M5 21V7l8-4v18"/>
                        <path d="M19 21V11l-6-4"/>
                      </svg>
                      For Manufacturers
                    </a>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <a href="/login" style={{
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '15px',
                      padding: '10px 20px',
                      borderRadius: '10px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}>
                      Login
                    </a>

                    <a href="/register" style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: 'white',
                      padding: '12px 24px',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '15px',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <line x1="19" y1="8" x2="19" y2="14"/>
                        <line x1="22" y1="11" x2="16" y2="11"/>
                      </svg>
                      Get Started
                    </a>
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                display: isMobile ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-enter" style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderBottom: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            padding: '20px',
            zIndex: 40
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {isAuthenticated ? (
                <>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>
                      Welcome, {user?.firstName}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '14px', textTransform: 'capitalize' }}>
                      {isManufacturer ? 'Manufacturer Account' : 'Consumer Account'}
                    </div>
                  </div>

                  {isManufacturer ? (
                    <>
                      <a href="/manufacturer/dashboard" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Dashboard
                      </a>
                      <a href="/manufacturer/products" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Products
                      </a>
                      <a href="/manufacturer/analytics" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Analytics
                      </a>
                      <a href="/manufacturer/billing" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Billing
                      </a>
                    </>
                  ) : (
                    <>
                      <a href="/verify" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Verify Products
                      </a>
                      <a href="/dashboard" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                        Dashboard
                      </a>
                    </>
                  )}

                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      padding: '12px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <a href="/verify" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                    Verify Product
                  </a>
                  <a href="/about" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                    About
                  </a>
                  <a href="/manufacturer/register" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                    For Manufacturers
                  </a>
                  <a href="/login" style={{ color: '#475569', textDecoration: 'none', padding: '12px 0', fontWeight: '600' }}>
                    Login
                  </a>
                  <a href="/register" style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    padding: '12px 20px',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    Get Started
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
